import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FaBars, FaTimes, FaUserTie, FaAngleDown } from "react-icons/fa";
import NotificationPanel from "../notifications/NotificationPanel";
import { useAuth } from "../../context/AuthContext";

function FloatingNavbar() {
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [showProfileDropdown, setShowProfileDropdown] = useState(false);

    const {
        user,
        activeRole,
        userRoles,
        logout,
        switchRole,
    } = useAuth();

    const handleLogout = () => {
        logout();
        navigate("/");
    };

    const mapBackendRoleToUI = (role) => {
        switch (role?.toLowerCase()) {
            case "project coordinator":
                return "pc";
            case "thrustarea coordinator":
                return "thrustarea";
            case "guide":
                return "guide";
            case "examiner":
                return "examiner";
            default:
                return role;
        }
    };

    const navigateToRoleHome = (role) => {
        const map = {
            student: "/student/home",
            sa: "/student/home",
            guide: "/guide/home",
            pc: "/pc/home",
            admin: "/admin/home",
            thrustarea: "/thrustarea/home",
            examiner: "/examiner/home",
        };
        navigate(map[role] || "/");
    };

    const handleRoleChange = async (role) => {
        if (!role) return;
        setShowDropdown(false);
        const uiRole = mapBackendRoleToUI(role);
        const newRole = await switchRole(uiRole);
        if (newRole) navigateToRoleHome(newRole);
        setMobileOpen(false);
    };

    const formatRoleLabel = (role) => {
        if (!role) return "";
        switch (role.toLowerCase()) {
            case "pc":
                return "Project Coordinator";
            case "thrustarea":
                return "Thrust Area";
            case "sa":
                return "Super Admin";
            default:
                return role
                    .split(" ")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ");
        }
    };

    const linkClass = (path) =>
        `px-4 py-2 rounded-md font-semibold transition block
        ${location.pathname === path
            ? "text-[#b7202e] bg-[#b7202e14]"
            : "text-gray-700 hover:text-[#b7202e] hover:bg-[#b7202e0d]"
        }`;

    const renderNavLinks = (onClick) => {
        switch (activeRole) {
            case "student":
            case "sa":
                return (
                    <>
                        <Link to="/student/home" onClick={onClick} className={linkClass("/student/home")}>Home</Link>
                        <Link to="/student/guides" onClick={onClick} className={linkClass("/student/guides")}>Guides</Link>
                        <Link to="/student/experts" onClick={onClick} className={linkClass("/student/experts")}>Experts</Link>
                        <Link to="/student/progress" onClick={onClick} className={linkClass("/student/progress")}>Progress</Link>
                        <Link to="/student/downloads" onClick={onClick} className={linkClass("/student/downloads")}>Downloads</Link>
                        <Link to="/student/team" onClick={onClick} className={linkClass("/student/team")}>Team</Link>
                    </>
                );

            case "guide":
                return (
                    <>
                        <Link to="/guide/home" onClick={onClick} className={linkClass("/guide/home")}>Home</Link>
                        <Link to="/guide/progress" onClick={onClick} className={linkClass("/guide/progress")}>Progress</Link>
                        <Link to="/guide/downloads" onClick={onClick} className={linkClass("/guide/downloads")}>Downloads</Link>
                        <Link to="/guide/team" onClick={onClick} className={linkClass("/guide/team")}>Team</Link>
                    </>
                );

            case "pc":
                return (
                    <>
                        <Link to="/pc/home" onClick={onClick} className={linkClass("/pc/home")}>Home</Link>
                        <Link to="/pc/project" onClick={onClick} className={linkClass("/pc/project")}>Projects</Link>
                        <Link to="/pc/announcements" onClick={onClick} className={linkClass("/pc/announcements")}>Announcements</Link>
                        <Link to="/pc/expert" onClick={onClick} className={linkClass("/pc/expert")}>Expert</Link>
                        <Link to="/pc/report" onClick={onClick} className={linkClass("/pc/report")}>Report</Link>
                        <Link to="/pc/team" onClick={onClick} className={linkClass("/pc/team")}>Team</Link>
                    </>
                );

            case "admin":
                return (
                    <>
                        <Link to="/admin/home" onClick={onClick} className={linkClass("/admin/home")}>Home</Link>
                        <Link to="/admin/groups" onClick={onClick} className={linkClass("/admin/groups")}>Groups</Link>
                        <Link to="/admin/announcements" onClick={onClick} className={linkClass("/admin/announcements")}>Announcements</Link>
                        <Link to="/admin/report" onClick={onClick} className={linkClass("/admin/report")}>Report</Link>
                        <Link to="/admin/team" onClick={onClick} className={linkClass("/admin/team")}>Team</Link>
                    </>
                );

            case "thrustarea":
                return (
                    <>
                        <Link to="/thrustarea/home" onClick={onClick} className={linkClass("/thrustarea/home")}>Home</Link>
                        <Link to="/thrustarea/project" onClick={onClick} className={linkClass("/thrustarea/project")}>Projects</Link>
                        <Link to="/thrustarea/announcements" onClick={onClick} className={linkClass("/thrustarea/announcements")}>Announcements</Link>
                        <Link to="/thrustarea/report" onClick={onClick} className={linkClass("/thrustarea/report")}>Report</Link>
                        <Link to="/thrustarea/team" onClick={onClick} className={linkClass("/thrustarea/team")}>Team</Link>
                    </>
                );

            case "examiner":
                return (
                    <>
                        <Link to="/examiner/home" onClick={onClick} className={linkClass("/examiner/home")}>Home</Link>
                        <Link to="/examiner/evaluations" onClick={onClick} className={linkClass("/examiner/evaluations")}>Evaluations</Link>
                        <Link to="/examiner/report" onClick={onClick} className={linkClass("/examiner/report")}>Reports</Link>
                        <Link to="/examiner/team" onClick={onClick} className={linkClass("/examiner/team")}>Team</Link>
                    </>
                );

            default:
                return null;
        }
    };

    return (
        <nav className="fixed top-3 left-1/2 -translate-x-1/2 w-[95%] max-w-7xl bg-white/95 backdrop-blur-md shadow-lg rounded-xl z-50">
            <div className="flex items-center justify-between px-4 py-3">
                <img src="/images/Group 12.svg" alt="Logo" className="h-8" />

                <div className="hidden xl:flex items-center gap-1">
                    {renderNavLinks()}
                </div>

                <div className="flex items-center gap-3">
                    <NotificationPanel />

                    {/* Profile Dropdown */}
                    <div
                        className="relative hidden xl:block"
                        onMouseEnter={() => setShowProfileDropdown(true)}
                        onMouseLeave={() => setShowProfileDropdown(false)}
                    >
                        <button
                            className="flex items-center justify-center p-2 text-gray-700 hover:text-[#b7202e] transition"
                        >
                            <FaUserTie className="text-xl" />
                        </button>

                        {showProfileDropdown && (
                            <div className="absolute right-0 mt-0 w-56 bg-white rounded-lg shadow-xl border border-gray-100 p-4 z-50">
                                <div className="flex flex-col gap-1">
                                    <p className="font-semibold text-gray-800 text-sm m-0">{user?.name || "User"}</p>
                                    <p className="text-xs text-gray-500 m-0 break-all">{user?.email}</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Role Dropdown */}
                    {userRoles?.length > 1 && (
                        <div className="relative hidden xl:block">
                            <button
                                onClick={() => setShowDropdown(!showDropdown)}
                                className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-gray-50 transition"
                            >
                                <span className="text-[#b7202e] font-semibold text-sm">
                                    {formatRoleLabel(activeRole)}
                                </span>
                                <FaAngleDown className={`text-gray-500 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
                            </button>

                            {showDropdown && (
                                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-100 py-1 z-50">
                                    {userRoles.map((role) => (
                                        <button
                                            key={role}
                                            onClick={() => handleRoleChange(role)}
                                            className={`w-full text-left px-4 py-2 text-sm transition flex items-center gap-2
                                                ${role === activeRole
                                                    ? "bg-[#b7202e]/10 text-[#b7202e] font-medium"
                                                    : "text-gray-700 hover:bg-[#b7202e] hover:text-white"
                                                }`}
                                        >
                                            {formatRoleLabel(role)}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <button
                        onClick={handleLogout}
                        className="hidden xl:block bg-[#b7202e] text-white px-4 py-1.5 rounded-md font-semibold hover:bg-[#9a1e27] transition"
                    >
                        Logout
                    </button>

                    <button
                        className="xl:hidden text-2xl"
                        onClick={() => setMobileOpen(!mobileOpen)}
                    >
                        {mobileOpen ? <FaTimes /> : <FaBars />}
                    </button>
                </div>
            </div>

            {mobileOpen && (
                <div className="xl:hidden border-t bg-white rounded-b-xl px-4 py-3 space-y-3">
                    <div className="flex flex-col gap-1">
                        {renderNavLinks(() => setMobileOpen(false))}
                    </div>

                    {userRoles?.length > 1 && (
                        <div className="pt-2 border-t">
                            <p className="text-sm font-semibold mb-1">Switch Role</p>
                            <div className="flex flex-wrap gap-2">
                                {userRoles.map((r) => (
                                    <button
                                        key={r}
                                        onClick={() => handleRoleChange(r)}
                                        className={`px-3 py-1.5 rounded-md text-sm border
                                            ${r === activeRole
                                                ? "bg-[#b7202e] text-white"
                                                : "bg-gray-100"
                                            }`}
                                    >
                                        {r}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <button
                        onClick={handleLogout}
                        className="w-full mt-3 bg-[#b7202e] text-white py-2 rounded-md font-semibold"
                    >
                        Logout
                    </button>
                </div>
            )}
        </nav>
    );
}

export default FloatingNavbar;
